import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { dataService } from '../../../Services/data/data.service';
import { makeStyles } from "@material-ui/core/styles";
import { ChevronDown, Check, CircleOff, X } from 'lucide-react';
import { IconButton, Select, FormControl, MenuItem, InputLabel, ListSubheader } from '@mui/material';
import { externalApiTypes } from '../../../Config/constants';
import { set } from 'date-fns';

function AccountCategoryDropdown({ accountCategories, selectedAccountCategoryId, setSelectedAccountCategoryId, handleCreateNewAccountCategory, error, ...props }) {
    const classes = useStyles();

    // Dropdown
    const [openDropdown, setOpenDropdown] = useState(false);
    const openDropdownButtonRef = useRef(null);
    const dropdownRef = useRef(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [dropdownWidth, setDropdownWidth] = useState(0);

    // Input
    const [inputValue, setInputValue] = useState("");
    const [categoryExists, setCategoryExists] = useState(true); // is there a category with the exact name of inputValue? 

    // AccountCategory
    const [allAccountCategories, setAllAccountCategories] = useState([]);
    const [filteredAccountCategories, setFilteredAccountCategories] = useState([]);
    const [selectedAccountCategory, setSelectedAccountCategory] = useState(null);

    // Creating a new category
    const [creatingNewCategory, setCreatingNewCategory] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState("");
    const [newCategoryIsRevenue, setNewCategoryIsRevenue] = useState(true);

    useEffect(() => {
        // TODO: Initial setup
    }, [])

    useEffect(() => {
        if (accountCategories !== allAccountCategories) {
            setAllAccountCategories(accountCategories);
            if (inputValue !== "") {
                let filtered = accountCategories.filter((category) => {
                    return category.name.toLowerCase().includes(inputValue.toLowerCase());
                });

                setFilteredAccountCategories(filtered);
            } else {
                setFilteredAccountCategories(accountCategories);
            }
        }
    }, [accountCategories])

    useEffect(() => {
        if (openDropdown && openDropdownButtonRef.current) {
            const rect = openDropdownButtonRef.current.getBoundingClientRect();

            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4, // Adjust for scroll position
                left: rect.left + window.scrollX, // Adjust for horizontal scrolling
            });

            setDropdownWidth(rect.width);
        }
    }, [openDropdown]);

    useEffect(() => {
        if (!selectedAccountCategory || selectedAccountCategoryId !== selectedAccountCategory.id) {
            // find selected category in accountCategories
            let category = accountCategories.find((category) => {
                return category.id === selectedAccountCategoryId;
            });

            setSelectedAccountCategory(category);
            setInputValue(category ? category.name : "");
        }
    }, [selectedAccountCategoryId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && openDropdown) {
                // check if target is not the button
                if (!openDropdownButtonRef.current.contains(event.target)) {
                    setOpenDropdown(false);
                };
            };
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    const resetCreatingNewCategory = () => {
        setCreatingNewCategory(false);
        setNewCategoryName("");
        setNewCategoryIsRevenue(true);
    };

    const resetInput = () => {
        handleInputValue("");
        setSelectedAccountCategory(null);
        setSelectedAccountCategoryId(null);
    };

    const handleInputValue = (value) => {
        let exists = true;

        if (value !== "") {
            setOpenDropdown(true);
        }
        resetCreatingNewCategory();
        setInputValue(value);

        // check if exact inputValue exists in accountCategories
        if (value !== "") {
            exists = accountCategories.find((category) => {
                return category.name.toLowerCase() === value.toLowerCase();
            });
        };

        setCategoryExists(exists ? true : false);

        // filter accountCategories and set filteredAccountCategories
        let filtered = accountCategories.filter((category) => {
            return category.name.toLowerCase().includes(value.toLowerCase());
        });

        setFilteredAccountCategories(filtered);
    }

    const handleAccountCategorySelection = (accountCategory) => {
        setSelectedAccountCategory(accountCategory); // Set selected category in state
        setSelectedAccountCategoryId(accountCategory.id); // Set selected category in parent component
        setInputValue(accountCategory.name); // Set input to name of selected category
        setOpenDropdown(false); // Close dropdown
    };

    const handleCreateAccountCategory = (categoryName) => {
        setCreatingNewCategory(true);
        setNewCategoryName(categoryName);
        setOpenDropdown(false);

        let newCategory = {
            name: categoryName,
            isRevenue: newCategoryIsRevenue,
        };

        handleCreateNewAccountCategory(newCategory); // CreateAccountDialog.jsx function
    };

    const handleSetNewCategoryIsRevenue = (isRevenue) => {
        setNewCategoryIsRevenue(isRevenue);

        let newCategory = {
            name: newCategoryName,
            isRevenue: isRevenue,
        };

        handleCreateNewAccountCategory(newCategory); // CreateAccountDialog.jsx function
    };

    return (
        <div className={classes.container}>
            <div
                ref={openDropdownButtonRef}
                className={classes.inputContainer}
                style={{ borderColor: error ? "#D84949" : "#B8B8B8" }}
            // onClick={() => setOpenDropdown(!openDropdown)}
            >
                <input
                    className={classes.input}
                    value={inputValue}
                    placeholder="Select a category..."
                    onChange={(e) => handleInputValue(e.target.value)} // Allow typing in input
                    onClick={(e) => setOpenDropdown(true)} // Prevent div click from toggling dropdown
                />
                {inputValue === "" ?
                    <ChevronDown onClick={() => setOpenDropdown(!openDropdown)} className={classes.input__icon} style={{ transform: `rotate(${openDropdown ? '180deg' : '0deg'})` }} />
                    :
                    <button className={classes.input__iconButton}><X onClick={() => resetInput()} className={classes.iconButton__icon} /></button>
                }
            </div>
            {openDropdown &&
                ReactDOM.createPortal(
                    <div
                        ref={dropdownRef}
                        className={classes.dropdownContainer}
                        style={{ top: dropdownPosition.top, left: dropdownPosition.left, width: dropdownWidth }}
                    >
                        {!categoryExists ?
                            <button className={classes.dropdown__createButton} onClick={() => handleCreateAccountCategory(inputValue)}>
                                Create <div className={classes.chip__grey}>{inputValue}</div>
                            </button>
                            :
                            null
                        }
                        {filteredAccountCategories.length > 0 ?
                            filteredAccountCategories.map((category, index) => (
                                <button className={classes.dropdown__menuItem} onClick={() => handleAccountCategorySelection(category)} key={"Type_" + index}>
                                    {category.name}
                                </button>
                            ))
                            :
                            <div className={classes.dropdown__emptyState}>
                                <CircleOff className={classes.emptyState__icon} />
                                <p className={classes.emptyState__text}>There are no categories to select</p>
                            </div>
                        }
                    </div>,
                    document.body
                )
            }
            {creatingNewCategory &&
                <div className={classes.inputRow}>
                    <label className={classes.labelText}>Revenue/expense *</label>
                    <div className={classes.radioButtonContainer}>
                        <div className={classes.radioButton} onClick={() => handleSetNewCategoryIsRevenue(true)}>
                            <div className={classes.radio} style={{ backgroundColor: `${newCategoryIsRevenue ? "var(--color-primary)" : "transparent"}`, border: `${newCategoryIsRevenue ? "none" : "1px solid #0C0E29"}` }}>
                                <Check className={classes.radioIcon} style={{ display: `${newCategoryIsRevenue ? "block" : "none"}` }} size={14} />
                            </div>
                            <p className={classes.radioButton__label}>Revenue</p>
                        </div>
                        <div className={classes.radioButton} onClick={() => handleSetNewCategoryIsRevenue(false)}>
                            <div className={classes.radio} style={{ backgroundColor: `${!newCategoryIsRevenue ? "var(--color-primary)" : "transparent"}`, border: `${!newCategoryIsRevenue ? "none" : "1px solid #0C0E29"}` }}>
                                <Check className={classes.radioIcon} style={{ display: `${!newCategoryIsRevenue ? "block" : "none"}` }} size={14} />
                            </div>
                            <p className={classes.radioButton__label}>Expense</p>
                        </div>
                    </div>
                </div>
            }
        </div>
    );
}

export default AccountCategoryDropdown

const useStyles = makeStyles({
    container: {
        position: "relative",
        width: "100%",

        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },

    inputContainer: {
        width: "100%",
        height: "100%",
        boxSizing: "border-box",

        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        border: "1px solid",
        borderRadius: "4px",
        backgroundColor: "transparent",

        padding: "0px",

        transition: "border-color 0.2s",

        "&:hover": {
            border: "1px solid #0C0E29",
            cursor: "cursor",
        },
    },

    input: {
        width: "100%",
        height: "40px",
        padding: "0 14px",
        paddingRight: "32px",
        border: "none",
        borderRadius: "4px",
        outline: "none",
        backgroundColor: "transparent",
        color: "#0C0E29",
        fontSize: "16px",
        textAlign: "left",

        "&:hover": {
            cursor: "pointer",
        },
    },

    dropdownContainer: {
        width: '100%',
        minWidth: "160px",
        maxHeight: '200px',
        overflowY: 'auto',
        position: 'absolute',
        // top: 'calc(100% + 4px)',
        // left: '0',
        zIndex: '9999',
        backgroundColor: '#FFFFFF',
        borderRadius: '4px',
        border: '1px solid #DCDCDC',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',

        display: 'flex',
        flexDirection: 'column',

        padding: '4px',

        "&::-webkit-scrollbar": {
            width: "4px",
        },

        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#989898",
            borderRadius: "2px",
        },

        "&::-webkit-scrollbar-track": {
            backgroundColor: "#F4F4F4",
            borderRadius: "2px",
        },

        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#7C7C7C",
        },
    },

    dropdown__collapseButton: {
        width: '100%',
        minHeight: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        gap: "8px",
        justifyContent: 'space-between',
        alignItems: 'center',

        padding: '0px 8px',
        border: "none",
        borderRadius: '4px',
        cursor: 'pointer',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "rgba(12, 14, 41, 0.6)",
        backgroundColor: '#FFFFFF',

        // no text wrapping
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',

        "&:hover": {
            backgroundColor: '#F4F4F4',
        },

        "&:active": {
            backgroundColor: '#EDEDED',
        },

        "&:focus": {
            outline: "none",
        },
    },

    collapseButton__icon: {
        width: "16px",
        height: "16px",
        color: "#0C0E29",
    },

    dropdown__menuItem: {
        width: '100%',
        minHeight: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',

        padding: '0px 8px',
        border: "none",
        borderRadius: '4px',
        cursor: 'pointer',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",
        backgroundColor: '#FFFFFF',

        "&:hover": {
            backgroundColor: '#F4F4F4',
        },

        "&:active": {
            backgroundColor: '#EDEDED',
        },

        "&:focus": {
            outline: "none",
        },
    },

    dropdown__createButton: {
        width: '100%',
        minHeight: '32px',
        boxSizing: 'border-box',

        display: 'flex',
        alignItems: 'center',
        gap: "8px",

        padding: '0px 8px',
        border: "none",
        borderRadius: '4px',
        cursor: 'pointer',

        fontSize: '16px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'normal',
        color: "#0C0E29",
        backgroundColor: '#F4F4F4',

        "&:hover": {
            backgroundColor: '#EDEDED',

            // style chip grey on hover
            "& > div": {
                backgroundColor: '#DCDCDC',
            },
        },

        "&:active": {
            backgroundColor: '#EDEDED',
        },

        "&:focus": {
            outline: "none",
        },


    },

    chip__grey: {
        width: "fit-content",
        height: "22px",
        display: "inline-flex",
        alignItems: "center",
        padding: "0px 8px",
        fontSize: "14px",
        fontFamily: "Lato, sans-serif",
        borderRadius: "12px",
        backgroundColor: "#EDEDED",
        color: "#0C0E29",
        transition: "background-color 0.2s",
    },

    dropdown__emptyState: {
        height: "fit-content",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "24px 0 16px 0",
    },

    emptyState__icon: {
        width: "24px",
        height: "24px",
        color: "#0C0E29",

        strokeWidth: "1.4px",
    },

    emptyState__text: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",
        margin: "0",
    },

    input__iconButton: {
        height: "32px",

        position: "absolute",
        right: "4px",
        top: "4px",

        display: "flex",
        gap: "8px",
        alignItems: "center",

        border: "none",
        borderRadius: "4px",

        fontSize: "16px",
        color: "#0C0E29",
        backgroundColor: "transparent",

        transition: "all 0.2s ease-in-out",

        "&:hover": {
            backgroundColor: "#F8F8F8",
            color: "#D84949",
            cursor: "pointer",
        }
    },

    iconButton__icon: {
        height: "20px",
        width: "20px",

        display: "block",
    },

    input__icon: {
        position: "absolute",
        right: "10px",
        width: "20px",
        height: "20px",
        color: "#0C0E29",
    },

    icon: {
        width: "20px",
        height: "20px",
        strokeWidth: "1.4px",
        color: "#0C0E29",
    },

    inputRow: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },

    radioButtonContainer: {
        width: "100%",
        display: "flex",
        gap: "8px",
    },

    radioButton: {
        width: "fit-content",
        height: "40px",

        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",

        padding: "8px",

        borderRadius: "4px",

        transition: "all ease-in-out 0.2s",

        "&:hover": {
            backgroundColor: "#EDEDED",
            cursor: "pointer",
        }
    },

    radio: {
        width: "20px",
        height: "20px",

        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        boxSizing: "border-box",

        borderRadius: "50%",
        border: "1px solid #0C0E29",
        backgroundColor: "transparent",
    },

    radioIcon: {
        color: "#F8F8F8",
    },

    radioButton__label: {
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        color: "#0C0E29",

        margin: "0",
    },
});
